import {
    type ComponentType,
    PureComponent,
    type ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";
import { type ConstructorClass } from "../../../../types/internal/utility";
import UUID from "../../Generic/UUID";

export interface HelpArticle {
    scrollAnchor?: string;
    zendeskId: string;
}

export abstract class HelpContent {
    public readonly abstract title: string;
    public readonly abstract zendeskLabel: string;
    public readonly abstract articles: Record<string, HelpArticle>;
}

export type HelpCallback = () => void;

export interface HelpSection {
    title: string;
    articles: Array<HelpSectionArticle>;
}

export interface HelpSectionArticle extends HelpArticle {
    name: string;
}

export class HelpManager {
    protected articleCache = new Map<string, Array<HelpSectionArticle>>();

    protected sections = new Map<string, HelpContent>();
    protected listeners = new Set<HelpCallback>();

    protected async loadArticles(label: string): Promise<Array<HelpSectionArticle>> {
        if(process.env.NODE_ENV === "test") {
            return [];
        }

        const cached = this.articleCache.get(label);

        if(cached) {
            return cached;
        }

        const results: Array<HelpSectionArticle> = [];

        try {
            let url = `https://support.onshopfront.com/api/v2/help_center/en-us/articles.json?label_names=${encodeURIComponent(label)}&page[size]=100`;
            let hasNextPage = true;

            while(hasNextPage) {
                // Load the page
                const response = await fetch(url);

                if(!response.ok) {
                    throw await response.json();
                }

                const data = await response.json();

                hasNextPage = data.meta.has_more;
                url = data.links.next;

                // Prepare the articles
                for(const article of data.articles) {
                    results.push({
                        name     : article.title,
                        zendeskId: article.id,
                    });
                }
            }

            this.callListeners();
        } catch(err) {
            console.error(`Unable to load ${label} articles`, err);
        }

        this.articleCache.set(label, results);

        return results;
    }

    public addHelp(id: string, content: HelpContent): void {
        // Start the preload of the articles
        void this.loadArticles(content.zendeskLabel);

        this.sections.set(id, content);
        this.callListeners();
    }

    public removeHelp(id: string): void {
        this.sections.delete(id);
        this.callListeners();
    }

    protected callListeners(): void {
        for(const listener of this.listeners) {
            listener();
        }
    }

    public getSections(): Array<HelpSection> {
        const results: Array<HelpSection> = [];

        for(const section of this.sections.values()) {
            const articles: Array<HelpSectionArticle> = [];
            const used = new Set<string>();

            // Prepare the existing articles
            for(const name in section.articles) {
                if(!Object.prototype.hasOwnProperty.call(section.articles, name)) {
                    continue;
                }

                const article = section.articles[name];

                used.add(article.zendeskId.toString());
                articles.push({
                    ...article,
                    name,
                });
            }

            const serverArticles = this.articleCache.get(section.zendeskLabel);

            if(serverArticles) {
                for(const article of serverArticles) {
                    // Don't add the article if it already exists
                    if(used.has(article.zendeskId.toString())) {
                        continue;
                    }

                    articles.push(article);
                }
            }

            // Sort the articles
            articles.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });

            // Add the section
            results.push({
                title: section.title,
                articles,
            });
        }

        // Sort and return the sections
        return results.sort(
            (a, b) => a.title.localeCompare(b.title)
        );
    }

    public hasSections(): boolean {
        return this.sections.size > 0;
    }

    public addListener(callback: HelpCallback): void {
        this.listeners.add(callback);
    }

    public removeListener(callback: HelpCallback): void {
        this.listeners.delete(callback);
    }

    // Singleton
    private static instance: HelpManager;
    public static getInstance(): HelpManager {
        if(typeof this.instance === "undefined") {
            this.instance = new HelpManager();
        }

        return this.instance;
    }
}

export function useHelpManager(): HelpManager {
    return useMemo(() => {
        return HelpManager.getInstance();
    }, []);
}

export function useHelpSections(): Array<HelpSection> {
    const [ sections, setSections ] = useState<Array<HelpSection>>([]);

    const helpManager = useHelpManager();

    useEffect(() => {
        const onSectionsChange = () => {
            setSections(helpManager.getSections());
        };

        setSections(helpManager.getSections());

        helpManager.addListener(onSectionsChange);

        return () => {
            helpManager.removeListener(onSectionsChange);
        };
    }, [ helpManager ]);

    return sections;
}

export function useHelp(helpContent: ConstructorClass<HelpContent>): void {
    const id = useMemo(() => {
        return UUID.generate();
    }, []);

    const [ content, setContent ] = useState(new helpContent());

    useEffect(() => {
        if(!(content instanceof helpContent)) {
            setContent(new helpContent());
        }
    }, [ content, helpContent ]);

    useEffect(() => {
        // Register updated help content
        HelpManager.getInstance().addHelp(id, content);

        return () => {
            // Remove old version of help content
            HelpManager.getInstance().removeHelp(id);
        };
    }, [ id, content ]);
}

export function withHelp<T>(
    WrappedComponent: ComponentType<T>,
    helpContent: ConstructorClass<HelpContent>
): ComponentType<T> {
    const id = UUID.generate();
    const content = new helpContent();

    return class extends PureComponent<T> {
        public displayName = "WithHelp";

        public componentDidMount(): void {
            HelpManager.getInstance().addHelp(id, content);
        }

        public componentWillUnmount() {
            HelpManager.getInstance().removeHelp(id);
        }

        public render(): ReactNode {
            return (
                <WrappedComponent {...this.props} />
            );
        }
    };
}
